
import type { CubeRecipes, CubeResult } from "../net/messages";
import type { Inventory } from "../net/connection";
import { formatYang } from "../money";
import { itemArtNode } from "./itemart";
import { iconCellsTall } from "./itemicon";
import { elem, installChrome, styleInstaller } from "./chrome";

export type { CubeRecipes, CubeResult };

export interface CubeActions {
  /** Build whatever is on the tray. The panel sends the cells and waits. */
  onMake?: (slots: readonly number[]) => void;
  /** Walk away. Nothing is sent; the server holds no window to cancel. */
  onCancel?: () => void;
}

/** `CUBE_MAX_NUM` (`cube.h:12`): cells the counter holds. A twenty-fifth
 *  distinct entry names a cell the original's own fixed array could not have
 *  had, and the server refuses the whole tray for it. */
export const MAX_TRAY = 24;

/** `CubeWindow.CUBE_SLOT_COUNTX`/`CUBE_SLOT_COUNTY` (`uicube.py:159-160`):
 *  the tray's own shape. `8 * 3 === MAX_TRAY`. */
export const TRAY_COLUMNS = 8;
export const TRAY_ROWS = 3;

/** `ItemSlot` `x_step` / `y_step` on CubeSlot (`cubewindow.py`). */
export const TRAY_SLOT_SIZE = 32;

const STYLE_ID = "cube-style";

const STYLE = `
/* CubeWindow's 285x521 board is new_cube_bg (cubewindow.py:17-18, 24-34). */
.cube-panel {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 60;
  box-sizing: border-box;
  width: 285px;
  max-width: 90vw;
  height: 521px;
  max-height: 78vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 14px;
  border-radius: 12px;
  background: url("/ui/textures/locale/new_cube_bg.png") 0 0 / 285px 521px no-repeat rgba(12, 14, 18, 0.94);
  border: 1px solid rgba(170, 226, 255, 0.22);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  font: 13px/1.5 system-ui, sans-serif;
  color: #d8dee6;
}
.cube-title {
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #849b9b;
  margin-bottom: 8px;
}
.cube-who { font-size: 1rem; color: #7ad2ff; margin-bottom: 10px; }
/* The board stays put and only the result list scrolls, as CubeWindow's
   contentScrollbar pages its result slots (uicube.py:118-150, 179-186). */
.cube-panel > * { flex: 0 0 auto; }
.cube-panel > .cube-recipes { flex: 1 1 auto; min-height: 0; overflow-y: auto; }
.cube-section {
  margin: 10px 0 0;
  padding: 8px 0 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.cube-section-label {
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #849b9b;
  margin-bottom: 5px;
}
.cube-recipe {
  padding: 5px 6px;
  border-radius: 6px;
  border: 1px solid transparent;
}
.cube-recipe-ready { border-color: rgba(143, 209, 138, 0.35); }
.cube-recipe-head { display: flex; justify-content: space-between; gap: 12px; align-items: center; }
.cube-recipe-reward {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.cube-recipe-reward .item-icon {
  height: 22px;
  margin: 0;
  flex: 0 0 auto;
}
.cube-recipe-name { color: #ffd27a; }
.cube-line { display: flex; justify-content: space-between; gap: 12px; align-items: center; }
.cube-line-short { color: #e0736a; }
.cube-materials { margin-top: 3px; padding-left: 8px; font-size: 0.9rem; }
.cube-material {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  cursor: pointer;
}
.cube-material:first-child { margin-top: 0; }
.cube-material-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex: 0 0 auto;
}
.cube-material-icon .item-icon {
  height: 20px;
  margin: 0;
}
.cube-material .cube-line { flex: 1 1 auto; }
/* CubeWindow.CUBE_SLOT_COUNTX/_COUNTY (uicube.py:159-160): 8 columns, 3 rows,
   24 fixed 32px ItemSlot cells -- the same shape MAX_TRAY bounds below. */
.cube-tray {
  display: grid;
  grid-template-columns: repeat(${TRAY_COLUMNS}, ${TRAY_SLOT_SIZE}px);
  grid-template-rows: repeat(${TRAY_ROWS}, ${TRAY_SLOT_SIZE}px);
  width: ${TRAY_COLUMNS * TRAY_SLOT_SIZE}px;
  height: ${TRAY_ROWS * TRAY_SLOT_SIZE}px;
  gap: 0;
}
.cube-pick { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
.cube-pick button {
  padding: 2px 4px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: transparent;
  color: #d8dee6;
  font: inherit;
  font-size: 0.78rem;
  cursor: pointer;
  min-height: 32px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cube-tray-cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 0;
  border: 1px solid rgba(122, 210, 255, 0.4);
  border-radius: 0;
  background: transparent;
  color: #7ad2ff;
  font: inherit;
  font-size: 0.78rem;
  cursor: pointer;
  overflow: visible;
}
.cube-tray-cell .item-icon {
  position: absolute;
  top: 0;
  left: 0;
  width: ${TRAY_SLOT_SIZE}px;
  height: calc(${TRAY_SLOT_SIZE}px * var(--cube-item-span, 1));
  margin: 0;
  pointer-events: none;
  z-index: 1;
}
.cube-tray-count {
  position: absolute;
  right: 1px;
  bottom: 0;
  font-size: 0.65rem;
  line-height: 1;
  color: #ffd27a;
  pointer-events: none;
  z-index: 2;
}
.cube-tray-cell-empty {
  color: #4f5b63;
  border-color: rgba(255, 255, 255, 0.08);
  cursor: default;
  overflow: hidden;
}
.cube-tray-cell-covered { cursor: pointer; }
.cube-tray-cell:hover:not(.cube-tray-cell-empty),
.cube-pick button:hover { background: rgba(255, 255, 255, 0.08); }
.cube-empty { color: #7c848d; font-style: italic; }
.cube-buttons { display: flex; gap: 8px; margin-top: 12px; }
.cube-button:disabled { opacity: 0.45; cursor: default; }
.cube-result { margin-top: 10px; font-size: 0.9rem; }
.cube-result-good { color: #8fd18a; }
.cube-result-bad { color: #e0736a; }
.cube-blend { margin-top: 3px; color: #c8b6ff; font-size: 0.85rem; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE, installChrome);


export function describeBlend(result: CubeResult): string {
  if (!result.blend_apply) return "";
  const minutes = Math.round(result.blend_duration / 60);
  return `${result.blend_apply} +${result.blend_value} for ${minutes} min`;
}

export class CubePanel {
  private readonly root: HTMLElement;
  private readonly who: HTMLElement;
  private readonly list: HTMLElement;
  private readonly trayRow: HTMLElement;
  private readonly pick: HTMLElement;
  private readonly build: HTMLButtonElement;
  private readonly result: HTMLElement;
  private readonly blend: HTMLElement;
  private frame: CubeRecipes | null = null;
  private bag: Inventory | null = null;
  private tray: number[] = [];

  constructor(
    container: HTMLElement,
    private readonly actions: CubeActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);

    this.root = elem(doc, "div", "cube-panel");
    this.root.hidden = true;
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "false");

    const title = elem(doc, "div", "cube-title");
    title.id = "cube-title";
    title.textContent = "Build";
    this.root.setAttribute("aria-labelledby", title.id);

    this.who = elem(doc, "div", "cube-who");

    const recipesSection = elem(doc, "div", "cube-section cube-recipes");
    const recipesLabel = elem(doc, "div", "cube-section-label", "What this counter makes");
    this.list = doc.createElement("div");
    recipesSection.append(recipesLabel, this.list);

    const traySection = elem(doc, "div", "cube-section");
    const trayLabel = elem(doc, "div", "cube-section-label", "On the counter");
    this.trayRow = elem(doc, "div", "cube-tray");
    this.pick = elem(doc, "div", "cube-pick");
    traySection.append(trayLabel, this.trayRow, this.pick);

    this.result = elem(doc, "div", "cube-result");
    this.result.hidden = true;
    this.blend = elem(doc, "div", "cube-blend");
    this.blend.hidden = true;

    const buttons = elem(doc, "div", "cube-buttons");

    this.build = doc.createElement("button");
    this.build.type = "button";
    this.build.className = "cube-button m2-middle";
    this.build.textContent = "Build";
    this.build.addEventListener("click", () => this.make());

    const cancel = doc.createElement("button");
    cancel.type = "button";
    cancel.className = "cube-button cube-button-cancel m2-middle";
    cancel.textContent = "Cancel";
    cancel.addEventListener("click", () => this.cancel());

    buttons.append(this.build, cancel);
    this.root.append(
      title,
      this.who,
      recipesSection,
      traySection,
      buttons,
      this.result,
      this.blend,
    );
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !this.showing) return;
      event.preventDefault();
      event.stopPropagation();
      this.cancel();
    };
    this.root.addEventListener("keydown", onEscape);
    doc.addEventListener("keydown", (event) => {
      if (!this.root.isConnected) return;
      onEscape(event);
    });
    container.append(this.root);
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  /** The counter this panel is open at, or zero. */
  get openAt(): number {
    return this.frame === null ? 0 : this.frame.npc_id;
  }

  /** What Build would send. A copy, so a caller cannot reach in and rearrange
   *  the counter. */
  get onCounter(): readonly number[] {
    return [...this.tray];
  }

  get canBuild(): boolean {
    return !this.build.disabled;
  }

  setBag(bag: Inventory): void {
    this.bag = bag;
    if (!this.showing || this.frame === null) return;
    this.redrawTray();
  }

  open(recipes: CubeRecipes, bag: Inventory): void {
    this.frame = recipes;
    this.bag = bag;
    this.tray = [];
    this.result.hidden = true;
    this.blend.hidden = true;
    this.who.textContent = recipes.npc_name;

    this.drawRecipes();

    this.redrawTray();
    this.root.hidden = false;
  }

  private drawRecipes(): void {
    const recipes = this.frame;
    const bag = this.bag;
    if (recipes === null || bag === null) return;

    const doc = this.root.ownerDocument;
    this.list.replaceChildren();
    if (recipes.recipes.length === 0) {
      const none = elem(doc, "div", "cube-empty", "Nothing.");
      this.list.append(none);
    }
    for (const recipe of recipes.recipes) {
      const row = elem(doc, "div", recipe.ready ? "cube-recipe cube-recipe-ready" : "cube-recipe");

      const head = elem(doc, "div", "cube-recipe-head");
      const reward = elem(doc, "span", "cube-recipe-reward");
      // The painting, or this reward's class silhouette. `reward_kind` is
      // `ground.py:item_kind`'s byte, sent because twelve of the shipped
      // table's rewards have no TGA in any 2014 pack and they are not all
      // the same class (`tests/test_icon_kind_on_the_wire.py`).
      reward.append(itemArtNode(recipe.reward_vnum, recipe.reward_kind, doc));
      const name = elem(doc, "span", "cube-recipe-name");
      name.textContent =
        recipe.reward_count > 1
          ? `${recipe.reward_name} x${recipe.reward_count}`
          : recipe.reward_name;
      reward.append(name);
      const cost = doc.createElement("span");
      cost.textContent = `${formatYang(recipe.gold)} · ${recipe.chance}%`;
      head.append(reward, cost);
      row.append(head);

      const materials = elem(doc, "div", "cube-materials");
      for (const material of recipe.materials) {
        const matRow = elem(doc, "div", "cube-material");
        const iconCell = elem(doc, "div", "cube-material-icon");
        iconCell.append(itemArtNode(material.vnum, material.kind, doc));
        matRow.append(
          iconCell,
          line(
            doc,
            material.name,
            `${material.have} / ${material.need}`,
            material.have < material.need,
          ),
        );
        matRow.addEventListener("click", () => this.addMaterial(material.vnum, material.need));
        materials.append(matRow);
      }
      row.append(materials);
      this.list.append(row);
    }
  }

  /** `__OnSelectMaterialSlot` (`uicube.py:237-330`): put the first bag cell
   *  holding `need` of `vnum` that is not on the counter yet onto it. */
  private addMaterial(vnum: number, need: number): void {
    const held = this.bag?.slots.find(
      (s) => s.vnum === vnum && s.count >= need && !this.tray.includes(s.slot),
    );
    if (held !== undefined) this.toggle(held.slot);
  }

  toggle(slot: number): void {
    if (this.frame === null) return;
    const at = this.tray.indexOf(slot);
    if (at >= 0) {
      this.tray.splice(at, 1);
    } else {
      if (this.tray.length >= MAX_TRAY) return;
      const held = this.bag?.slots.find((s) => s.slot === slot);
      const vnum = held?.vnum ?? 0;
      if (firstFreeTrayCell(this.trayLayout().originOf, traySpan(vnum)) < 0) return;
      this.tray.push(slot);
    }
    this.redrawTray();
  }

  settle(outcome: CubeResult): void {
    this.tray = [];
    this.redrawTray();
    this.result.hidden = false;
    this.result.className = `cube-result ${outcome.succeeded ? "cube-result-good" : "cube-result-bad"}`;
    const made =
      outcome.reward_count > 1
        ? `${outcome.reward_name} x${outcome.reward_count}`
        : outcome.reward_name;
    if (!outcome.succeeded) {
      this.result.textContent = "It came apart. The ingredients are gone.";
    } else if (outcome.dropped > 0) {
      // `AutoGiveItem`'s fallback made visible: a full bag does not refuse the
      // build, so some of it is on the floor and the player has to be told, or
      // they walk away from it.
      this.result.textContent = `Made ${made}. ${outcome.dropped} would not fit and is on the ground.`;
    } else {
      this.result.textContent = `Made ${made}.`;
    }
    if (outcome.gold_spent > 0) {
      this.result.textContent += ` Spent ${formatYang(outcome.gold_spent)}.`;
    }

    // Cleared as well as hidden: `hidden` is a presentation attribute and the
    // text is still in the tree behind it, which is exactly the sort of thing
    // a screen reader reads out and a test cannot see the difference of.
    const blend = outcome.succeeded ? describeBlend(outcome) : "";
    this.blend.hidden = blend === "";
    this.blend.textContent = blend;
  }

  /** Take the window down. Used when the player walks away, dies or logs out. */
  close(): void {
    this.root.hidden = true;
    this.frame = null;
    this.bag = null;
    this.tray = [];
    this.result.hidden = true;
    this.blend.hidden = true;
    this.list.replaceChildren();
    this.trayRow.replaceChildren();
    this.pick.replaceChildren();
  }

  /** Send the build. Public so a keyboard shortcut can reach it. */
  make(): void {
    const frame = this.frame;
    if (frame === null || this.tray.length === 0) return;
    const slots = [...this.tray];
    // Cleared before the send, so a double-click cannot produce two builds off
    // one counter. The second would be refused anyway -- the ingredients are
    // spent by then -- but a window that lets a player ask twice is a window
    // that looks like it charged them twice.
    this.tray = [];
    this.redrawTray();
    this.actions.onMake?.(slots);
  }

  cancel(): void {
    this.close();
    this.actions.onCancel?.();
  }

  private redrawTray(): void {
    const doc = this.root.ownerDocument;
    const bag = this.bag;
    this.trayRow.replaceChildren();
    this.pick.replaceChildren();

    // 24 cells, always -- `CUBE_SLOT_COUNTX * CUBE_SLOT_COUNTY`
    // (`uicube.py:159-160`). Origins paint size>1 icons down their column;
    // covered cells stay in the track and click back to the origin bag slot.
    const layout = this.trayLayout();
    const byOrigin = new Map(layout.origins.map((o) => [o.cell, o]));

    for (let cell = 0; cell < MAX_TRAY; cell += 1) {
      const originCell = layout.originOf[cell];
      if (originCell === undefined) {
        const empty = elem(doc, "span", "cube-tray-cell cube-tray-cell-empty", "·");
        this.trayRow.append(empty);
        continue;
      }
      const origin = byOrigin.get(originCell);
      if (origin === undefined) {
        const empty = elem(doc, "span", "cube-tray-cell cube-tray-cell-empty", "·");
        this.trayRow.append(empty);
        continue;
      }
      const bagSlot = origin.bagSlot;
      const button = doc.createElement("button");
      button.type = "button";
      const covered = originCell !== cell;
      button.className = covered
        ? "cube-tray-cell cube-tray-cell-covered"
        : "cube-tray-cell";
      const held = bag?.slots.find((s) => s.slot === bagSlot);
      const label = labelFor(bag, bagSlot);
      button.title = label;
      if (!covered) {
        button.style.setProperty("--cube-item-span", String(origin.span));
        button.style.zIndex = origin.span > 1 ? "1" : "";
        const art = held === undefined ? null : itemArtNode(held.vnum, held.kind, doc);
        if (art !== null) {
          button.append(art);
          if (held !== undefined && held.count > 1) {
            const count = elem(doc, "span", "cube-tray-count", String(held.count));
            button.append(count);
          }
        } else {
          // Unknown or missing icon: keep the text name rather than inventing art.
          button.textContent = label;
        }
      }
      button.addEventListener("click", () => this.toggle(bagSlot));
      this.trayRow.append(button);
    }

    if (bag !== null) {
      for (const held of bag.slots) {
        if (this.tray.includes(held.slot)) continue;
        const button = doc.createElement("button");
        button.type = "button";
        button.textContent =
          held.count > 1 ? `${held.name} x${held.count}` : held.name;
        button.addEventListener("click", () => this.toggle(held.slot));
        this.pick.append(button);
      }
    }

    this.build.disabled = this.tray.length === 0;
  }

  /** Size-aware ItemSlot packing of the current tray bag slots. */
  private trayLayout(): TrayLayout {
    const bag = this.bag;
    const items: TrayItem[] = [];
    for (const bagSlot of this.tray) {
      const held = bag?.slots.find((s) => s.slot === bagSlot);
      items.push({ bagSlot, vnum: held?.vnum ?? 0 });
    }
    return packTray(items);
  }
}

function labelFor(bag: Inventory | null, slot: number): string {
  const held = bag?.slots.find((s) => s.slot === slot);
  if (held === undefined) return `Cell ${slot}`;
  return held.count > 1 ? `${held.name} x${held.count}` : held.name;
}

function line(doc: Document, label: string, value: string, short: boolean): HTMLElement {
  const row = elem(doc, "div", short ? "cube-line cube-line-short" : "cube-line");
  const left = doc.createElement("span");
  left.textContent = label;
  const right = doc.createElement("span");
  right.textContent = value;
  row.append(left, right);
  return row;
}

interface TrayItem {
  readonly bagSlot: number;
  readonly vnum: number;
}

interface TrayOrigin {
  readonly cell: number;
  readonly bagSlot: number;
  readonly span: number;
}

interface TrayLayout {
  /** Grid cell → origin cell that paints over it (including itself). */
  readonly originOf: readonly (number | undefined)[];
  readonly origins: readonly TrayOrigin[];
}

/** Proto / icon height in tray rows, clamped to the 3-row CubeSlot. */
function traySpan(vnum: number): number {
  return Math.min(Math.max(1, iconCellsTall(vnum)), TRAY_ROWS);
}

/**
 * First-fit pack into the 8×3 CubeSlot the way `uicube.py` walks empty cells
 * looking for a column-run tall enough for `item.GetItemSize()`.
 */
function packTray(items: readonly TrayItem[]): TrayLayout {
  const originOf: (number | undefined)[] = Array.from({ length: MAX_TRAY });
  const origins: TrayOrigin[] = [];

  for (const item of items) {
    const span = traySpan(item.vnum);
    const cell = firstFreeTrayCell(originOf, span);
    if (cell < 0) continue;
    origins.push({ cell, bagSlot: item.bagSlot, span });
    for (let step = 0; step < span; step += 1) {
      originOf[cell + step * TRAY_COLUMNS] = cell;
    }
  }
  return { originOf, origins };
}

/**
 * First CubeSlot index that can hold an item `span` rows tall, or -1.
 * `originOf` marks cells already claimed by earlier tray entries.
 */
function firstFreeTrayCell(
  originOf: readonly (number | undefined)[],
  span: number,
): number {
  const tall = Math.min(Math.max(1, span), TRAY_ROWS);
  for (let cell = 0; cell < MAX_TRAY; cell += 1) {
    const row = Math.floor(cell / TRAY_COLUMNS);
    if (row + tall > TRAY_ROWS) continue;
    let free = true;
    for (let step = 0; step < tall; step += 1) {
      if (originOf[cell + step * TRAY_COLUMNS] !== undefined) {
        free = false;
        break;
      }
    }
    if (free) return cell;
  }
  return -1;
}
